import React from 'react';
import { Helmet } from 'react-helmet';
import { Box, Container, Grid, Typography, Button, Card, CardContent, Stack } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { getOrganizationSchema, getBreadcrumbSchema, getContactPageSchema } from '../utils/seoSchemas';
import './Contact.css';

const Contact = () => {
  const organizationSchema = {
    ...getOrganizationSchema(),
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+44-333-2101-500',
      email: 'hello@go-ops.digital',
      contactType: 'sales',
      areaServed: 'GB',
      availableLanguage: 'English'
    }
  };

  const contactPageSchema = getContactPageSchema();

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: 'Home', url: 'https://www.go-ops.digital/' },
    { name: 'Contact', url: 'https://www.go-ops.digital/contact' }
  ]);

  return (
    <Box className="contact-root">
      <Helmet>
        <title>Contact Go Ops</title>
        <meta
          name="description"
          content="Contact Go Ops to book a demo or learn how AI-powered garage operations can speed up approvals and reduce admin."
        />
        <link rel="canonical" href="https://www.go-ops.digital/contact" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Go Ops" />
        <meta property="og:title" content="Contact Go Ops" />
        <meta
          property="og:description"
          content="Contact Go Ops to book a demo or learn how AI-powered garage operations can speed up approvals and reduce admin."
        />
        <meta property="og:url" content="https://www.go-ops.digital/contact" />
        <meta property="og:image" content="https://www.go-ops.digital/goopslogo.jpeg" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Go Ops" />
        <meta
          name="twitter:description"
          content="Contact Go Ops to book a demo or learn how AI-powered garage operations can speed up approvals and reduce admin."
        />
        <meta name="twitter:image" content="https://www.go-ops.digital/goopslogo.jpeg" />
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(contactPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>
      <Container maxWidth="md">
        <Box className="contact-hero">
          <Typography variant="overline" color="text.secondary">
            Book a Demo
          </Typography>
          <Typography variant="h3" className="contact-title" gutterBottom>
            See Go Ops in action
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Tell us a bit about your garage and we’ll schedule a tailored walkthrough of the
            AI booking pipeline, job board, and payments flow.
          </Typography>

          <Card className="contact-card">
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Email</Typography>
                  <Typography variant="body2" color="text.secondary">
                    hello@go-ops.digital
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Phone</Typography>
                  <Typography variant="body2" color="text.secondary">
                    +44 333 2101 500
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">What you’ll get</Typography>
                  <Typography variant="body2" color="text.secondary">
                    A 30‑minute demo, tailored workflow review, and rollout plan for your garages.
                  </Typography>
                </Grid>
              </Grid>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3 }}>
                <Button
                  className="contact-button"
                  variant="contained"
                  component="a"
                  href="mailto:hello@go-ops.digital?subject=Go%20Ops%20Demo%20Request"
                >
                  Email to book
                </Button>
                <Button
                  className="contact-outline"
                  variant="outlined"
                  component={RouterLink}
                  to="/auth/login"
                >
                  Sign in
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </Box>
  );
};

export default Contact;
